/**
 * Create or edit one tag category: a group in the creators' tag palette. Switching it off hides its tags from
 * creators and makes scripts using them refused. Mount only while open (the page keys it per category).
 */

import { useMemo, useState, type ChangeEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { narrationApi } from '@api/endpoints/narration'
import type { AdminNarrationTagCategoryDto } from '@api/types/narration'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  type CategoryDraft,
  blankCategoryDraft,
  categoryDraftFromDto,
  categoryRequestFromDraft,
  narrationErrorMessage,
  narrationTagCategoriesKey,
  validateCategoryDraft,
} from './narrationForm'

export function NarrationTagCategoryDialog({
  category,
  nextSortOrder,
  existing,
  onClose,
}: {
  category: AdminNarrationTagCategoryDto | null
  nextSortOrder: number
  existing: readonly AdminNarrationTagCategoryDto[]
  onClose: () => void
}) {
  const creating = category === null
  const dialogProps = useMobileDialogProps()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<CategoryDraft>(() =>
    category ? categoryDraftFromDto(category) : blankCategoryDraft(nextSortOrder),
  )
  const [attempted, setAttempted] = useState(false)
  const errors = useMemo(() => validateCategoryDraft(draft, existing, category?.id), [draft, existing, category?.id])
  const shown = attempted ? errors : {}

  const save = useMutation({
    mutationFn: () => {
      const request = categoryRequestFromDraft(draft)
      return category ? narrationApi.tagCategories.update(category.id, request) : narrationApi.tagCategories.create(request)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: narrationTagCategoriesKey })
      onClose()
    },
  })

  const submit = () => {
    setAttempted(true)
    if (Object.keys(errors).length > 0) return
    save.mutate()
  }

  const set = <K extends keyof CategoryDraft>(key: K, value: CategoryDraft[K]) => setDraft((d) => ({ ...d, [key]: value }))
  const text = (key: 'name' | 'description' | 'sortOrder') => ({
    value: draft[key],
    onChange: (e: ChangeEvent<HTMLInputElement>) => set(key, e.target.value),
    error: Boolean(shown[key]),
    size: 'small' as const,
    fullWidth: true,
  })

  return (
    <Dialog open onClose={save.isPending ? undefined : onClose} maxWidth="xs" fullWidth {...dialogProps}>
      <DialogTitle>{creating ? 'New tag category' : `Edit ${category.name}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <TextField label="Name" {...text('name')} required helperText={shown.name ?? 'The tab creators pick in the tag palette.'} />
          <TextField
            label="Description"
            {...text('description')}
            multiline
            minRows={2}
            helperText={shown.description ?? 'One line shown under the tab, as a hint.'}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <Box>
              <FormControlLabel
                control={<Switch checked={draft.isActive} onChange={(e) => set('isActive', e.target.checked)} />}
                label="Active"
              />
              <Typography variant="caption" color="text.secondary" component="div">
                Inactive hides its tags from creators and refuses them in new generations.
              </Typography>
            </Box>
            <TextField label="Sort order" {...text('sortOrder')} type="number" helperText={shown.sortOrder ?? 'Lower first.'} />
          </Box>
          {attempted && Object.keys(errors).length > 0 && <Alert severity="warning">Fix the highlighted fields to save.</Alert>}
          {save.error && <Alert severity="error">{narrationErrorMessage(save.error, 'The category could not be saved')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : creating ? 'Add category' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
